import { Cron } from '@langchain/langgraph-sdk';
import {
  ClockPlusIcon,
  PlusIcon,
  Trash02Icon,
} from '@langchain/untitled-ui-icons';
import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import cronstrue from 'cronstrue';
import { useState } from 'react';

import { useGraphAssistant } from '@/Pages/Graph/src/api/assistants';
import { checkIsSystemAssistant } from '@/Pages/Graph/src/components/site/Assistants/utils';
import { DataGrid, DataGridPagination } from '@/components/DataGrid';
import { useDataGridSizingLocalStorage } from '@/components/DataGrid.utils';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal';
import EmptyState from '@/components/EmptyState';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { filterInputs } from '@/components/RunsTable/utils/filterInputs';
import { Skeleton } from '@/components/Skeleton';
import { TextOverflowTooltip } from '@/components/TextOverflowTooltip';
import { Button, IconButton } from '@/design-system';
import { useDataGridState } from '@/hooks/useDataGridState';
import { useSortingState } from '@/hooks/useSortingState';
import { RunInputSchema } from '@/types/schema';

import { CronSortBy, useCrons, useDeleteCron } from '../../hooks/useCrons';
import { CreateCronPane } from './CreateCronPane';

const columnHelper = createColumnHelper<Cron>();

const AssistantCell = ({ cron }: { cron: Cron }) => {
  const { assistant_id: assistantId } = cron;
  const { data: assistant, isLoading, error } = useGraphAssistant(assistantId);
  if (isLoading) return <Skeleton className="h-4 w-20" />;
  if (error || !assistant) {
    return (
      <TextOverflowTooltip tooltipMaxWidth={300}>
        {assistantId}
      </TextOverflowTooltip>
    );
  }
  return (
    <TextOverflowTooltip tooltipMaxWidth={300}>
      {checkIsSystemAssistant(assistant)
        ? `Default (${assistant.name})`
        : assistant.name}
    </TextOverflowTooltip>
  );
};

const NextRunDateCell = ({ cron }: { cron: Cron }) => {
  const { next_run_date: nextRunDate, end_time: endTime } = cron;
  const nextRunDateFormatted = (() => {
    if (!nextRunDate) {
      return 'N/A';
    }
    const nextDate = new Date(nextRunDate);
    const endDate = endTime ? new Date(endTime) : null;
    if (endDate && nextDate > endDate) {
      return 'Ended';
    }
    return nextDate.toLocaleString();
  })();

  return (
    <TextOverflowTooltip tooltipMaxWidth={300}>
      {nextRunDateFormatted}
    </TextOverflowTooltip>
  );
};

const COLUMNS_DEF: ColumnDef<Cron, any>[] = [
  columnHelper.accessor('cron_id', {
    id: 'cron_id',
    header: () => <span className="pl-4 text-sm text-tertiary">Cron ID</span>,
    size: 100,
    minSize: 150,
    maxSize: 300,
    cell: ({ row }) => (
      <TextOverflowTooltip tooltipMaxWidth={300} className="pl-4">
        {row.original.cron_id}
      </TextOverflowTooltip>
    ),
  }),
  columnHelper.accessor('next_run_date', {
    id: 'next_run_date',
    header: 'Next Run Date',
    size: 300,
    minSize: 250,
    cell: ({ row }) => <NextRunDateCell cron={row.original} />,
  }),
  columnHelper.accessor('schedule', {
    id: 'schedule',
    header: 'Schedule',
    size: 300,
    minSize: 250,
    enableSorting: false,
    cell: ({ row }) => (
      <TextOverflowTooltip tooltipMaxWidth={300}>
        {cronstrue.toString(row.original.schedule)}
      </TextOverflowTooltip>
    ),
  }),
  columnHelper.accessor('end_time', {
    id: 'end_time',
    header: 'End time',
    size: 250,
    minSize: 100,
    cell: ({ row }) => (
      <TextOverflowTooltip tooltipMaxWidth={300}>
        {row.original.end_time
          ? new Date(row.original.end_time).toLocaleString()
          : 'Never'}
      </TextOverflowTooltip>
    ),
  }),
  columnHelper.accessor('payload', {
    id: 'payload',
    header: 'Input',
    size: 250,
    minSize: 100,
    enableSorting: false,
    cell: ({ row }) => (
      <TextOverflowTooltip tooltipMaxWidth={300}>
        {filterInputs(row.original.payload.input as RunInputSchema) ||
          JSON.stringify(row.original.payload.input).slice(0, 100)}
      </TextOverflowTooltip>
    ),
  }),
  columnHelper.accessor('assistant_id', {
    id: 'assistant_id',
    header: 'Assistant',
    size: 150,
    minSize: 100,
    cell: ({ row }) => <AssistantCell cron={row.original} />,
  }),
  columnHelper.accessor('thread_id', {
    id: 'thread_id',
    header: 'Thread ID',
    size: 150,
    minSize: 100,
    cell: ({ row }) => (
      <TextOverflowTooltip tooltipMaxWidth={300}>
        {row.original.thread_id ?? 'N/A'}
      </TextOverflowTooltip>
    ),
  }),
  columnHelper.accessor('created_at', {
    id: 'created_at',
    header: 'Created At',
    size: 150,
    minSize: 100,
    cell: ({ row }) => (
      <TextOverflowTooltip tooltipMaxWidth={300}>
        {new Date(row.original.created_at).toLocaleString()}
      </TextOverflowTooltip>
    ),
  }),
  columnHelper.display({
    id: 'delete',
    cell: ({ row, table }) => {
      const { actions } = (table.options.meta ?? {}) as {
        actions: {
          handleDelete: (cronId: string) => void;
        };
      };

      return (
        <div className="flex items-center justify-end">
          <IconButton
            color="secondary"
            variant="plain"
            onClick={(e) => {
              e.stopPropagation();
              actions.handleDelete(row.original.cron_id);
            }}
            icon={Trash02Icon}
            label="Delete cron"
          />
        </div>
      );
    },
    size: 50,
  }),
];

export const CronsPanel = () => {
  'use no memo';
  const { paginationModel, setPaginationModel } = useDataGridState({
    defaultPaginationModel: {
      pageIndex: 0,
      pageSize: 10,
    },
  });
  const [sortingState, setSortingState] = useSortingState('created_at');

  const { data, isLoading, isValidating, error } = useCrons({
    limit: paginationModel.pageSize,
    offset: paginationModel.pageIndex * paginationModel.pageSize,
    sort_by: sortingState[0].id as CronSortBy,
    sort_order: sortingState[0].desc ? 'desc' : 'asc',
  });
  const { crons, total } = data ?? {};

  const [columnSizing, setColumnSizing] =
    useDataGridSizingLocalStorage('crons');

  const [deletingCronId, setDeletingCronId] = useState<string | null>(null);
  const { trigger: deleteCron } = useDeleteCron();

  const table = useReactTable({
    data: crons ?? [],
    columns: COLUMNS_DEF,
    state: {
      pagination: paginationModel,
      columnSizing,
      sorting: sortingState,
    },
    meta: {
      actions: {
        handleDelete: setDeletingCronId,
      },
    },
    getRowId: (row) => row.cron_id,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPaginationModel,
    onSortingChange: setSortingState,
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: 'onChange',
    manualFiltering: true,
    manualPagination: true,
    enableSorting: true,
    enableColumnResizing: true,
    pageCount: Math.ceil((total ?? 0) / paginationModel.pageSize),
  });

  const [showCreateCron, setShowCreateCron] = useState(false);

  if (error) {
    return <ExpandableErrorAlert error={error} />;
  }

  return (
    <>
      <div className="w-full overflow-hidden rounded-lg border border-secondary">
        <div className="flex items-center justify-between border-b border-secondary bg-secondary p-5">
          <div className="flex flex-col">
            <h2 className="text-lg font-semibold">Crons</h2>
            <p className="text-sm text-tertiary">
              Crons associated with this deployment
            </p>
          </div>
          <Button
            onClick={() => setShowCreateCron(true)}
            variant="outlined"
            color="secondary"
            size="sm"
            leftDecorator={PlusIcon}
          >
            New Cron
          </Button>
        </div>
        <div className="flex flex-col gap-3 overflow-y-auto">
          <DataGrid
            table={table}
            isLoading={isLoading || isValidating}
            stickyRightColumn
            emptyState={
              <div className="flex w-full flex-col items-center justify-center py-10">
                <EmptyState
                  title="Create your first Cron"
                  description="Schedule a Cron job to run on a regular basis."
                  icon={<ClockPlusIcon />}
                />
              </div>
            }
            loadingRowCount={paginationModel.pageSize}
            cellHeight={48}
          />
          <div className="p-4">
            <DataGridPagination table={table} />
          </div>
        </div>
      </div>
      <CreateCronPane
        isOpen={showCreateCron}
        setShowCreateCron={setShowCreateCron}
      />
      {deletingCronId && (
        <DeleteConfirmationModal
          isOpen={!!deletingCronId}
          onClose={() => setDeletingCronId(null)}
          title="Delete cron"
          description="Are you sure you want to delete this cron?"
          onConfirm={() => {
            deleteCron({ cronId: deletingCronId });
            setDeletingCronId(null);
          }}
        />
      )}
    </>
  );
};
